import { createContext } from "react";
import type { Session, User } from "@supabase/supabase-js";
import type { OnboardingStatus } from "@/types/onboarding";

export type AppRole =
  | "admin"
  | "partner"
  | "assessor"
  | "investidor"
  | "mfb"
  | "mfa"
  | "incorporador"
  | "diligencias_manager";

export interface Profile {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  cpf: string | null;
  phone: string | null;
  assessor_id: string | null;
  partner_id: string | null;
  incorporadora_id: string | null;
  role: AppRole | null;
  /** Especialidade do admin de diligência. null = admin principal (acesso total). */
  diligencia_especialidade: string | null;
  /** Status do onboarding do investidor */
  onboarding_status: OnboardingStatus | null;
  onboarding_completed_at: string | null;
  onboarding_step: string | null;
  available_to_invest: number | null;
}

export interface UserRole {
  role: AppRole;
}

export interface LGPDConsent {
  termsAccepted: boolean;
  privacyAccepted: boolean;
  marketingConsent: boolean;
  userAgent: string;
}

export interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  userRoles: UserRole[];
  isAdmin: boolean;
  isPartner: boolean;
  isAssessor: boolean;
  isInvestor: boolean;
  isMFB: boolean;
  isMFA: boolean;
  isIncorporador: boolean;
  isDiligenciasManager: boolean;
  primaryRole: AppRole | null;
  /** Papel escolhido manualmente na Tela 3 do login por PIN, quando o usuário tem mais de 1 role. */
  activeRoleOverride: AppRole | null;
  setActiveRole: (role: AppRole) => void;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    cpf: string,
    phone: string,
    lgpdConsent?: LGPDConsent
  ) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  /** Troca um token de magic-link (emitido pela Edge Function pin-login) por uma sessão real. */
  signInWithPinToken: (email: string, token: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<{ error: Error | null }>;
  updateProfileAvatar: (avatarUrl: string | null) => void;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };
